import { Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../apiConfig";
import { NAVIGATION_CONFIG, ROLE_TITLES } from "../../config/navigationConfig";

const Sidebar = ({ role, additionalData }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, companyName } = additionalData || location.state || {};
  
  const navItems = NAVIGATION_CONFIG[role] || [];
  const title = ROLE_TITLES[role] || "Menu";

  const handleNavigation = (href) => {
    if (role === "superadmin" && [
      "/superadmin/manage-leave-types",
      "/superadmin/companies",
      "/superadmin/users",
      "/superadmin/reports"
    ].includes(href)) {
      navigate(href, { state: { companyId, companyName } });
    } else {
      navigate(href);
    }
    setIsSidebarOpen(false);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("idToken");
    
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
    
    localStorage.removeItem("idToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("companyId");
    localStorage.removeItem("status");
    navigate("/");
  };

  return (
    <>
      <motion.button
        className="burger-menu-button"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsSidebarOpen(true)}
        style={{
          position: "fixed",
          top: "13px",
          left: "10px",
          zIndex: 999,
          background: "rgba(31, 41, 55, 0.8)",
          backdropFilter: "blur(8px)",
          border: "none",
          borderRadius: "50%",
          width: "48px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          color: "white"
        }}
      >
        <Menu size={24} />
      </motion.button>

      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 998,
              backdropFilter: "blur(2px)"
            }}
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            className="sidebar"
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{ 
              position: "fixed",
              top: 0,
              left: 0,
              width: "280px",
              height: "100vh",
              zIndex: 1000,
              boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
              background: "rgba(31, 41, 55, 0.9)",
              backdropFilter: "blur(10px)",
              borderRight: "1px solid #374151",
              padding: "20px 0",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              padding: "0 20px 20px",
              borderBottom: "1px solid #374151"
            }}>
              <h2 style={{ 
                margin: 0, 
                color: "white", 
                fontSize: "1.25rem",
                fontWeight: 600
              }}>
                {title}
              </h2>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsSidebarOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#9ca3af",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "8px"
                }}
              >
                <X size={20} />
              </motion.button>
            </div>

            {role === "superadmin" && companyName && (
              <div style={{
                padding: "12px 24px",
                borderBottom: "1px solid #374151",
                marginBottom: "8px"
              }}>
                <p style={{
                  margin: 0,
                  fontSize: "0.85rem",
                  color: "#9ca3af"
                }}>
                  Selected Company:
                </p>
                <p style={{
                  margin: "4px 0 0 0",
                  fontSize: "0.95rem",
                  color: "#f3f4f6",
                  fontWeight: 500
                }}>
                  {companyName}
                </p>
              </div>
            )}

            <nav className="sidebar-nav" style={{ marginTop: "20px", flex: 1 }}>
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <div 
                    key={item.href}
                    className="sidebar-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px 24px",
                      textDecoration: "none",
                      color: "white",
                      transition: "background-color 0.2s",
                      borderRadius: "8px",
                      margin: "4px 12px",
                      cursor: "pointer",
                      backgroundColor: isActive ? "rgba(99, 102, 241, 0.15)" : "transparent"
                    }}
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon
                      size={20}
                      className="sidebar-icon"
                      style={{ color: item.color, minWidth: "20px" }}
                    />
                    <span style={{ marginLeft: "16px", fontSize: "0.95rem" }}>
                      {item.name}
                    </span>
                    {isActive && (
                      <div style={{
                        position: "absolute",
                        right: "12px",
                        width: "4px",
                        height: "20px",
                        backgroundColor: item.color,
                        borderRadius: "4px"
                      }} />
                    )}
                  </div>
                );
              })}
            </nav>

            <div style={{
              padding: "16px 24px",
              borderTop: "1px solid #374151"
            }}>
              <div 
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "16px 24px",
                  color: "#EF4444",
                  transition: "background-color 0.2s",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: "rgba(239, 68, 68, 0.1)"
                }}
                onClick={handleLogout}
              >
                <LogOut size={20} style={{ minWidth: "20px" }} />
                <span style={{ marginLeft: "16px", fontSize: "0.95rem" }}>
                  Logout
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;